import { edge } from "@antv/g2plot/lib/adaptor/geometries";
import { register } from "@antv/x6-react-shape";
import { stroke } from "@antv/x6/lib/registry/highlighter/stroke";
import { Form, Image, Modal, Select, Typography } from "antd";
import { useContext, useEffect, useState } from "react";
import useFetch from "../../hooks/useFetch";

const { Text, Paragraph } = Typography;

const GOOD_STATUS = ["Good", "GOOD", "Online", "ONLINE"];
const DANGER_STATUS = ["Danger", "DANGER"];
const WARNING_STATUS = ["Warning", "WARNING"];
const OFFLINE_STATUS = ["Offline", "OFFLINE", undefined, null];

export default function registerPowerMeterNode() {
  const PowerMeterNode = ({ node, graph }) => {
    const label = node.getProp("label", "???");
    const display = node.getProp("display", "simple");
    const status = node.getProp("status", null);
    const data = node.getData();

    if (display == "simple") {
      node.setProp({ width: 60, height: 100 }, { silent: true });
    } else {
      node.setProp({ width: 270, height: 100 }, { silent: true });
    }

    const voltage = `${
      data?.["VLNavg"]?.["value"].toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) ?? "-"
    } ${data?.["VLNavg"]?.["unit"] ?? "V"}`;

    const current = `${
      data?.["Iavg"]?.["value"].toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) ?? "-"
    } ${data?.["Iavg"]?.["unit"] ?? "A"}`;

    const power = `${
      data?.["kWT"]?.["value"].toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) ?? "-"
    } ${data?.["kWT"]?.["unit"] ?? "kW"}`;

    const updateEdges = () => {
      const outgoingEdges = graph.getOutgoingEdges(node);

      const volt = data?.["VLNavg"]?.["value"] ?? 0;
      const amp = data?.["Iavg"]?.["value"] ?? 0;
      const pow = data?.["kWT"]?.["value"] ?? 0;

      outgoingEdges?.forEach((edge) => {
        edge.setAttrs({
          line: {
            stroke: volt != 0 ? "var(--ant-blue)" : "var(--ant-color-border)",
            strokeDasharray: 5,
            style: {
              animation: `ant-line ${
                pow >= 1000
                  ? 10
                  : pow >= 100
                  ? 30
                  : pow >= 10
                  ? 60
                  : pow > 0
                  ? 120
                  : 0
              }s infinite linear`,
            },
          },
        });
      });

      const incomingEdges = graph.getIncomingEdges(node);
      incomingEdges?.forEach((edge) => {
        const node = edge.getSourceNode();

        if (node != null || node.shape != "power-meter-detail") {
          edge.setAttrs({
            line: {
              stroke: volt != 0 ? "var(--ant-blue)" : "var(--ant-color-border)",
              strokeDasharray: 5,
              style: {
                animation: `ant-line ${
                  pow >= 1000
                    ? 10
                    : pow >= 100
                    ? 30
                    : pow >= 10
                    ? 60
                    : pow > 0
                    ? 120
                    : 0
                }s infinite linear`,
              },
            },
          });
        }
      });
    };

    updateEdges();

    const tableStyle = {
      position: "absolute",
      width: 200,
      top: 0,
      left: 75,
      color: "var(--ant-color-text-base)",
      borderWidth: "1px 0px 0px 1px",
      borderStyle: "solid",
      borderSpacing: 0,
      borderColor: "var(--ant-color-text-base)",
    };

    const tableColumnStyle = {
      minWidth: "99px",
      maxWidth: "100px",
      overflow: "hidden",
      textAlign: "center",
      borderWidth: "0px 1px 1px 0px",
      paddingInline: "4px",
      borderStyle: "solid",
      borderSpacing: 0,
      borderColor: "var(--ant-color-text-base)",
    };

    return (
      <>
        <div style={{ position: "relative" }}>
          <Image
            src="/power-meter.png"
            preview={false}
            style={{
              position: "absolute",
              top: 20,
              positionArea: "top left",
            }}
            width={60}
            height={60}
          ></Image>
          {display == "simple" && (
            <>
              <div
                style={{
                  position: "absolute",
                  textAlign: "center",
                  width: 120,
                  maxWidth: 120,
                  marginBottom: 0,
                  bottom: "calc(100% - 18px)",
                  left: -30,
                  transformOrigin: "center bottom",
                }}
              >
                <Paragraph
                  strong
                  ellipsis={{ tooltip: label, rows: 2 }}
                  style={{
                    color: "var(--ant-color-text-base)",
                    marginBottom: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {label}
                </Paragraph>
              </div>
              <div
                style={{
                  position: "absolute",
                  textAlign: "center",
                  width: 120,
                  maxWidth: 120,
                  top: 80,
                  left: -30,
                }}
              >
                <Text
                  strong
                  ellipsis={{ rows: 2, tooltip: label }}
                  style={{ color: "var(--ant-color-text-base)" }}
                >
                  {power}
                </Text>
              </div>
              <div
                style={{
                  backgroundColor: GOOD_STATUS.includes(status)
                    ? "var(--ant-color-success)"
                    : DANGER_STATUS.includes(status)
                    ? "var(--ant-color-error)"
                    : WARNING_STATUS.includes(status)
                    ? "var(--ant-color-waring)"
                    : OFFLINE_STATUS.includes(status)
                    ? "var(--ant-color-border)"
                    : "var(--ant-color-border)",
                  width: 14,
                  height: 14,
                  position: "absolute",
                  left: -16,
                  top: 43,
                  borderRadius: "50%",
                }}
              ></div>
            </>
          )}
          {display == "detailed" && (
            <>
              <div
                style={{
                  backgroundColor: GOOD_STATUS.includes(status)
                    ? "var(--ant-color-success)"
                    : DANGER_STATUS.includes(status)
                    ? "var(--ant-color-error)"
                    : WARNING_STATUS.includes(status)
                    ? "var(--ant-color-waring)"
                    : OFFLINE_STATUS.includes(status)
                    ? "var(--ant-color-border)"
                    : "var(--ant-color-border)",
                  width: 14,
                  height: 14,
                  position: "absolute",
                  left: 82,
                  top: 6,
                  borderRadius: "50%",
                }}
              ></div>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th
                      style={{ ...tableColumnStyle, paddingLeft: 24 }}
                      colSpan={2}
                    >
                      <Text
                        ellipsis={{ tooltip: label }}
                        style={{ color: "var(--ant-color-text-base)" }}
                      >
                        {label}
                      </Text>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th colSpan={1} style={tableColumnStyle}>
                      Voltage
                    </th>
                    <td colSpan={1} style={tableColumnStyle}>
                      <Text
                        ellipsis={{ tooltip: voltage }}
                        style={{ color: "var(--ant-color-text-base)" }}
                      >
                        {voltage}
                      </Text>
                    </td>
                  </tr>
                  <tr>
                    <th colSpan={1} style={tableColumnStyle}>
                      Current
                    </th>
                    <td colSpan={1} style={tableColumnStyle}>
                      <Text
                        ellipsis={{ tooltip: current }}
                        style={{ color: "var(--ant-color-text-base)" }}
                      >
                        {current}
                      </Text>
                    </td>
                  </tr>
                  <tr>
                    <th colSpan={1} style={tableColumnStyle}>
                      Power
                    </th>
                    <td colSpan={1} style={tableColumnStyle}>
                      <Text
                        ellipsis={{ tooltip: power }}
                        style={{ color: "var(--ant-color-text-base)" }}
                      >
                        {power}
                      </Text>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    );
  };

  register({
    shape: "power-meter-detail",
    width: 60,
    height: 100,
    effect: ["data", "status", "label", "status", "display"],
    component: PowerMeterNode,

    ports: {
      groups: {
        absolute: {
          position: { name: "absolute" },
          attrs: {
            circle: {
              r: 4,
              magnet: true,
              stroke: "#5F95FF",
              strokeWidth: 1,
              fill: "#fff",
              style: {
                visibility: "hidden",
              },
            },
          },
        },
      },
      items: [
        {
          group: "absolute",
          id: "left",
          args: {
            x: 5,
            y: 50,
          },
        },
        {
          group: "absolute",
          id: "top",
          args: {
            x: 30,
            y: 25,
          },
        },
        {
          group: "absolute",
          id: "right",
          args: {
            x: 55,
            y: 50,
          },
        },
        {
          group: "absolute",
          id: "bottom",
          args: {
            x: 30,
            y: 75,
          },
        },
      ],
    },
  });
}
